import { Box, Paper, Stack, styled, Typography } from '@mui/material';
import PageHeader from '../../controls/PageHeader';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import PhoneInput from '../../forms/PhoneInput';
import { useForm } from 'react-hook-form';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { useEffect } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as providerSlice from '../../../data/provider.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { lowerCase, upperCase } from 'lodash';
import {
  RideshareOutboundPhoneNumber,
  RideshareProvider,
} from '../../../data/models/provider-call-outcome.model';

const PREFIX = 'RideshareSettings';

const classes = {
  container: `${PREFIX}-container`,
  contentContainer: `${PREFIX}-contentContainer`,
  outboundCommContainer: `${PREFIX}-outboundCommContainer`,
  outboundCommDesc: `${PREFIX}-outboundCommDesc`,
  phoneNumbersContainer: `${PREFIX}-phoneNumbersContainer`,
  saveButton: `${PREFIX}-saveButton`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(1),
  },
  [`& .${classes.contentContainer}`]: {
    padding: theme.spacing(2),
    gap: theme.spacing(3),
    marginBottom: theme.spacing(5),
  },
  [`& .${classes.outboundCommContainer}`]: {
    width: 310,
    gap: theme.spacing(1),
  },
  [`& .${classes.outboundCommDesc}`]: {
    fontWeight: 600,
  },
  [`& .${classes.phoneNumbersContainer}`]: {
    gap: theme.spacing(3),
  },
  [`& .${classes.saveButton}`]: {
    width: 150,
  },
}));

export interface RideshareSettingsFormData {
  uberPhone?: string;
  lyftPhone?: string;
}

export default function RideshareSettings() {
  const dispatch = useAppDispatch();
  const t = useCommonTranslation();
  const isSuperReaderUser = useIsSuperReaderUser();
  const phoneNumbers = useAppSelector(
    providerSlice.selectRideshareOutboundPhoneNumbers,
  );
  const updatingRideshareNumbers = useAppSelector(
    providerSlice.selectUpdatingRideshareOutboundPhoneNumbers,
  );

  const formHook = useForm<RideshareSettingsFormData>({
    mode: 'onChange',
    defaultValues: {
      uberPhone: '',
      lyftPhone: '',
    },
  });

  useEffect(() => {
    dispatch(providerSlice.fetchRideshareOutboundPhoneNumbers());
  }, []);

  useEffect(() => {
    phoneNumbers.forEach((phoneNumber) => {
      formHook.setValue(
        `${lowerCase(phoneNumber.provider)}Phone` as any,
        phoneNumber.dispatchPhone,
      );
    });
  }, [phoneNumbers]);

  const handleSave = (formData: RideshareSettingsFormData) => {
    const phoneNumbersToUpdate: RideshareOutboundPhoneNumber[] = [];
    const rideshareProvidersToDelete: RideshareProvider[] = [];

    Object.entries(RideshareProvider).forEach(
      ([providerKey, providerValue]) => {
        const key = `${providerKey}Phone` as keyof RideshareSettingsFormData;
        let value = formData[key];
        if (value) {
          value = value.replace(/\s+/g, '');
        }
        const currentValue = phoneNumbers.find((phoneNumber) => {
          return phoneNumber.provider === providerValue;
        });

        if (value && value !== currentValue?.dispatchPhone) {
          phoneNumbersToUpdate.push({
            provider: providerValue as RideshareProvider,
            dispatchPhone: value,
          });
        } else if (!value && currentValue?.dispatchPhone) {
          rideshareProvidersToDelete.push(providerValue as RideshareProvider);
        }
      },
    );
    if (phoneNumbersToUpdate.length > 0) {
      phoneNumbersToUpdate.forEach((phoneNumber) => {
        dispatch(
          providerSlice.updateRideshareOutboundPhoneNumbers(phoneNumber),
        );
      });
    }
    if (rideshareProvidersToDelete.length > 0) {
      rideshareProvidersToDelete.forEach((provider) => {
        dispatch(providerSlice.deleteRideshareOutboundPhoneNumbers(provider));
      });
    }
  };

  const primaryActionText = isSuperReaderUser ? null : t('common.save');

  return (
    <StyledPaper>
      <Box className={classes.container}>
        <PageHeader
          title={t('rideshareSettings.title')}
          returnLink="/settings"
          returnLinkText={t('settings.title')}
          primaryActionText={primaryActionText}
          primaryActionDisabled={updatingRideshareNumbers}
          primaryActionClassName={classes.saveButton}
          onPrimaryAction={formHook.handleSubmit(handleSave)}
        />
        <Stack direction="row" className={classes.contentContainer}>
          <Stack direction="column" className={classes.outboundCommContainer}>
            <Typography variant="h3">
              {t('rideshareSettings.outboundComm')}
            </Typography>
            <Typography variant="body2" className={classes.outboundCommDesc}>
              {t('rideshareSettings.outboundCommDesc')}
            </Typography>
          </Stack>
          <Stack direction="column" className={classes.phoneNumbersContainer}>
            <PhoneInput
              name="uberPhone"
              label={t('rideshareSettings.uber')}
              control={formHook.control}
              readOnly={isSuperReaderUser}
              disableDropdown
            />
            <PhoneInput
              name="lyftPhone"
              label={t('rideshareSettings.lyft')}
              control={formHook.control}
              readOnly={isSuperReaderUser}
              disableDropdown
            />
          </Stack>
        </Stack>
      </Box>
    </StyledPaper>
  );
}
